import {UUID} from '../../../shared/types/uuid.type';

/**
 * Represents a transcoding preset with all encoding settings
 */
export interface TranscodingPreset {
  id: UUID;
  name: string;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;

  // Basic Settings
  scaleMode: VideoScaleMode;
  width: number | null;
  height: number | null;
  bitrateControlMode: BitrateControlMode;
  crf: number | null;
  videoBitrateKbps: number | null;
  bufferSizeKb: number | null;

  // Encoder Settings
  videoCodec: VideoCodec;
  videoPreset: H264Preset | null;
  videoProfile: H264Profile | null;
  deinterlacing: boolean;

  // GOP Structure
  keyframeAlignmentMode: KeyframeAlignmentMode;
  fps: VideoFPS | null;
  keyframeInterval: number | null;
  bFramesCount: number;

  // Audio Settings
  audioMode: AudioMode;
  audioBitrateKbps: AudioBitrateKbps | null;
  sampleRate: AudioSampleRate | null;
  normalize: boolean;

  // Subtitles & Metadata Forwarding
  forwardCEA708Subtitles: boolean;
  forwardDVBSubtitles: boolean;
  forwardWebVTTSubtitles: boolean;
  forwardSCTE35Markers: boolean;
  forwardKLVMetadata: boolean;
  forwardSEITimecodes: boolean;
}

/**
 * DTO for creating a new transcoding preset
 * Omits server-generated fields like id, createdAt, updatedAt
 */
export interface CreateTranscodingPresetDto {
  name: string;

  // Basic Settings
  scaleMode: VideoScaleMode;
  width: number | null;
  height: number | null;
  bitrateControlMode: BitrateControlMode;
  crf: number | null;
  videoBitrateKbps: number | null;
  bufferSizeKb: number | null;

  // Encoder Settings
  videoCodec: VideoCodec;
  videoPreset: H264Preset | null;
  videoProfile: H264Profile | null;
  deinterlacing: boolean;

  // GOP Structure
  keyframeAlignmentMode: KeyframeAlignmentMode;
  fps: VideoFPS | null;
  keyframeInterval: number | null;
  bFramesCount: number;

  // Audio Settings
  audioMode: AudioMode;
  audioBitrateKbps: AudioBitrateKbps | null;
  sampleRate: AudioSampleRate | null;
  normalize: boolean;

  // Subtitles & Metadata Forwarding
  forwardCEA708Subtitles: boolean;
  forwardDVBSubtitles: boolean;
  forwardWebVTTSubtitles: boolean;
  forwardSCTE35Markers: boolean;
  forwardKLVMetadata: boolean;
  forwardSEITimecodes: boolean;
}

/**
 * Video scaling modes
 */
export type VideoScaleMode =
  | 'scale_to_width'
  | 'scale_to_height'
  | 'fit_with_letterbox'
  | 'fill_and_crop';

/**
 * Video scale mode labels map
 */
export const videoScaleModeLabels: Map<VideoScaleMode, string> = new Map(
  [
    ['scale_to_width', 'Scale to Width'],
    ['scale_to_height', 'Scale to Height'],
    ['fit_with_letterbox', 'Fit with Letterbox'],
    ['fill_and_crop', 'Fill and Crop']
  ]
);

/**
 * Formats video scale mode to a human-readable label
 *
 * @param mode - The video scale mode value
 * @returns The formatted video scale mode label
 */
export function formatVideoScaleModeLabel(mode: VideoScaleMode): string {
  const label: string | undefined = videoScaleModeLabels.get(mode);
  if (!label) {
    return mode;
  }

  return label;
}

/**
 * Bitrate control modes
 */
export type BitrateControlMode = 'vbr' | 'crf' | 'cbr';

/**
 * Bitrate control mode labels map
 */
export const bitrateControlModeLabels: Map<BitrateControlMode, string> = new Map(
  [
    ['vbr', 'Variable Bitrate (VBR)'],
    ['crf', 'Constant Rate Factor (CRF)'],
    ['cbr', 'Constant Bitrate (CBR)']
  ]
);

/**
 * Formats bitrate control mode to a human-readable label
 *
 * @param mode - The bitrate control mode value
 * @returns The formatted bitrate control mode label
 */
export function formatBitrateControlModeLabel(mode: BitrateControlMode): string {
  const label: string | undefined = bitrateControlModeLabels.get(mode);
  if (!label) {
    return mode.toUpperCase();
  }

  return label;
}

/**
 * Supported video codecs
 */
export type VideoCodec = 'h264' | 'h265';

/**
 * Video codec labels map
 */
export const videoCodecLabels: Map<VideoCodec, string> = new Map(
  [
    ['h264', 'H.264 (AVC)'],
    ['h265', 'H.265 (HEVC)']
  ]
);

/**
 * Formats video codec to a human-readable label
 *
 * @param codec - The video codec value
 * @returns The formatted video codec label
 */
export function formatVideoCodecLabel(codec: VideoCodec): string {
  const label: string | undefined = videoCodecLabels.get(codec);
  if (!label) {
    return codec.toUpperCase();
  }

  return label;
}

/**
 * H.264/H.265 encoding presets
 */
export type H264Preset =
  | 'ultrafast'
  | 'superfast'
  | 'veryfast'
  | 'faster'
  | 'fast'
  | 'medium'
  | 'slow'
  | 'slower'
  | 'veryslow';

/**
 * H.264 encoding profiles
 */
export type H264Profile =
  | 'baseline'
  | 'main'
  | 'high'
  | 'high10'
  | 'high422'
  | 'high444';

/**
 * H.264 encoding profile labels map
 */
export const h264ProfileLabels: Map<H264Profile, string> = new Map(
  [
    ['baseline', 'Baseline'],
    ['main', 'Main'],
    ['high', 'High'],
    ['high10', 'High 10'],
    ['high422', 'High 4:2:2'],
    ['high444', 'High 4:4:4']
  ]
);

/**
 * Formats H.264 profile to a human-readable label
 *
 * @param profile - The H.264 profile value
 * @returns The formatted H.264 profile label
 */
export function formatH264ProfileLabel(profile: H264Profile | null): string {
  if (!profile) {
    return '';
  }

  const label: string | undefined = h264ProfileLabels.get(profile);
  if (!label) {
    return profile.toUpperCase();
  }

  return label;
}


/**
 * Keyframe alignment modes
 */
export type KeyframeAlignmentMode =
  | 'passthrough'
  | 'fixed_fps'
  | 'fixed_gop';

/**
 * Supported video frame rates
 */
export type VideoFPS = 23.976 | 24 | 25 | 29.97 | 30 | 50 | 59.94 | 60;

/**
 * Audio processing modes
 */
export type AudioMode = 'passthrough' | 'aac_stereo';

/**
 * Audio mode labels map
 */
export const audioModeLabels: Map<AudioMode, string> = new Map(
  [
    ['passthrough', 'Passthrough'],
    ['aac_stereo', 'AAC (stereo)']
  ]
);

/**
 * Formats audio mode to a human-readable label
 *
 * @param mode - The audio mode value
 * @returns The formatted audio mode label
 */
export function formatAudioModeLabel(mode: AudioMode): string {
  const label: string | undefined = audioModeLabels.get(mode);
  if (!label) {
    return mode;
  }

  return label;
}

/**
 * Supported audio bitrates in Kbps
 */
export type AudioBitrateKbps = 48 | 64 | 96 | 128 | 160 | 192 | 256 | 320;

/**
 * Supported audio sample rates in Hz
 */
export type AudioSampleRate = 44100 | 48000;
